/**
 * D75 — AI Meeting Notes page.
 *
 * Three areas:
 *   1. Recorder panel (top)   — Mic permission → record → auto-upload.
 *   2. Notes list (left)      — All recorded meetings (newest first).
 *   3. Active note view (right) — 3 tabs: Summary / Notes / Transcript.
 *
 * Status flow (polled every 5s via meetingNotesAPI.status):
 *   queued → transcribing → summarizing → ready (or failed)
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NotionEditor } from '../components/NotionEditor';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Mic, Square, Loader2, Sparkles, FileText, Trash2,
  Download, CheckSquare, ListChecks, AlertCircle, Clock, Users, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { meetingNotesAPI } from '../lib/api';
import {
  stashPendingRecording, listPendingRecordings, removePendingRecording,
} from '../lib/pendingMeetingUploads';
import ShareWithControl from '../components/ShareWithControl';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

const STATUS_LABELS = {
  queued:       { label: 'Queued',        color: 'text-slate-400' },
  transcribing: { label: 'Transcribing…', color: 'text-sky-400'   },
  summarizing:  { label: 'Summarizing…',  color: 'text-amber-400' },
  ready:        { label: 'Ready',         color: 'text-emerald-400' },
  failed:       { label: 'Failed',        color: 'text-red-400'   },
};

function fmtDuration(sec) {
  if (!sec || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function MeetingNotesPage() {
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeNote, setActiveNote] = useState(null);
  const [loading, setLoading] = useState(true);

  // Recorder state
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // D75 BUG-1 — Pending recordings stashed in IndexedDB while their
  // upload is in flight. We surface them in the rail so a refresh
  // doesn't lose the audio, with a Retry button per row.
  const [pendingUploads, setPendingUploads] = useState([]);
  const [retryingId, setRetryingId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // ── Load pending recordings from IndexedDB on mount ─────────────
  const refreshPending = useCallback(async () => {
    try {
      const rows = await listPendingRecordings();
      setPendingUploads(Array.isArray(rows) ? rows : []);
    } catch { /* IDB unavailable — fine, safety net only */ }
  }, []);
  useEffect(() => { refreshPending(); }, [refreshPending]);

  // ── Load notes list ─────────────────────────────────────────────
  const refreshList = useCallback(async () => {
    try {
      const res = await meetingNotesAPI.list();
      setNotes(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refreshList(); }, [refreshList]);

  // D75 BUG-1/2 — Poll the LIST while any note is still processing so
  // the rail status (uploading → transcribing → summarizing → ready)
  // updates without forcing the user to click into the note.
  useEffect(() => {
    const inFlight = notes.some(
      (n) => ['queued', 'uploading', 'transcribing', 'summarizing'].includes(n.processing_status));
    if (!inFlight) return;
    const t = setInterval(refreshList, 4000);
    return () => clearInterval(t);
  }, [notes, refreshList]);

  // ── Load + poll active note ─────────────────────────────────────
  useEffect(() => {
    if (!activeId) { setActiveNote(null); return; }
    let alive = true;
    let interval;
    const load = async () => {
      try {
        const res = await meetingNotesAPI.get(activeId);
        if (!alive) return;
        setActiveNote(res.data);
        const st = res.data?.processing_status;
        if (st === 'ready' || st === 'failed') {
          clearInterval(interval);
          // Final state — also refresh the list to show updated title.
          refreshList();
        }
      } catch {
        if (alive) clearInterval(interval);
      }
    };
    load();
    interval = setInterval(load, 5000);
    return () => { alive = false; clearInterval(interval); };
  }, [activeId, refreshList]);

  // ── Recording ────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      // iOS Safari favours mp4/m4a; everywhere else webm/Opus
      let mimeType = 'audio/webm;codecs=opus';
      if (typeof MediaRecorder !== 'undefined' && !MediaRecorder.isTypeSupported(mimeType)) {
        const fallback = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav'];
        mimeType = fallback.find((m) => MediaRecorder.isTypeSupported(m)) || '';
      }
      const mr = mimeType ? new MediaRecorder(stream, { mimeType })
                          : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || mimeType || 'audio/webm' });
        // Free mic immediately
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        await uploadRecording(blob, mr.mimeType || mimeType || 'audio/webm');
      };
      mr.start(1000);
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (e) {
      toast.error('Microphone permission denied or unavailable');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    timerRef.current = null;
    setRecording(false);
  };

  const uploadRecording = async (blob, mime, opts = {}) => {
    setUploading(true);
    setUploadPct(0);
    const ext = mime.includes('mp4') ? 'm4a'
              : mime.includes('mpeg') ? 'mp3'
              : mime.includes('wav') ? 'wav'
              : 'webm';

    // D75 BUG-1 — Stash the blob to IndexedDB BEFORE the network upload
    // starts. If the user refreshes mid-upload (or the network drops),
    // the audio survives and the page surfaces a Retry button on mount.
    // `opts.pendingId` is set when this is a retry of an already-stashed
    // blob so we don't double-write the row.
    let pendingId = opts.pendingId || null;
    if (!pendingId) {
      try {
        pendingId = await stashPendingRecording({ blob, mime, ext });
        await refreshPending();
      } catch (e) {
        console.warn('[d75] stash failed, proceeding without safety net:', e);
      }
    }

    try {
      const fd = new FormData();
      fd.append('file', blob, `meeting-${Date.now()}.${ext}`);
      const res = await meetingNotesAPI.upload(fd, (ev) => {
        if (ev.total) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
      });
      const newId = res.data.id;
      // Upload succeeded → the server has the audio. We can safely
      // drop the IndexedDB safety net now.
      if (pendingId) {
        try { await removePendingRecording(pendingId); } catch { /* noop */ }
        await refreshPending();
      }
      toast.success('Upload complete — transcribing in background');
      await refreshList();
      setActiveId(newId);
      setRecordingTime(0);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Upload failed';
      toast.error(`${msg} — your recording is saved locally. Use Retry in the list to upload again.`);
      // KEEP the IndexedDB row; user can retry via the pending list.
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  // D75 BUG-1 — Retry a stashed pending recording. Pull the Blob from
  // IndexedDB and re-run the upload path.
  const retryPending = async (row) => {
    if (!row?.blob) {
      toast.error('No audio data available — removing entry');
      try { await removePendingRecording(row.id); } catch { /* noop */ }
      await refreshPending();
      return;
    }
    setRetryingId(row.id);
    try {
      await uploadRecording(row.blob, row.mime || 'audio/webm', { pendingId: row.id });
    } finally {
      setRetryingId(null);
    }
  };

  const discardPending = async (row) => {
    if (!window.confirm('Discard this unsaved recording? This cannot be undone.')) return;
    try { await removePendingRecording(row.id); } catch { /* noop */ }
    await refreshPending();
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="flex h-full bg-background text-foreground" data-testid="meeting-notes-page">
      {/* Left rail: list + recorder */}
      <aside className="w-[320px] border-r border-border flex flex-col">
        <div className="p-4 border-b border-border space-y-2">
          <div className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-gold" />
            <h2 className="font-semibold">AI Note Taker</h2>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Records audio → AI transcript → English summary
          </p>
          {!recording && !uploading && (
            <Button
              data-testid="start-recording-btn"
              className="w-full bg-red-600 text-white hover:bg-red-700"
              onClick={startRecording}
            >
              <Mic className="h-4 w-4 mr-2" /> Start Recording
            </Button>
          )}
          {recording && (
            <Button
              data-testid="stop-recording-btn"
              className="w-full bg-red-700 text-white hover:bg-red-800 animate-pulse"
              onClick={stopRecording}
            >
              <Square className="h-4 w-4 mr-2 fill-current" />
              Stop · {fmtDuration(recordingTime)}
            </Button>
          )}
          {uploading && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              <div className="flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Uploading audio… {uploadPct}%
              </div>
            </div>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {loading && (
              <div className="px-3 py-4 text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            )}
            {/* D75 BUG-1 — Pending uploads stashed in IndexedDB. These
                are recordings whose upload was interrupted (refresh,
                network drop, etc.). Surfaced so user can Retry or Discard. */}
            {pendingUploads.length > 0 && (
              <div className="mb-2 border border-amber-500/30 bg-amber-500/5 rounded-md p-2" data-testid="pending-uploads-section">
                <div className="text-[11px] font-semibold text-amber-300 mb-1.5 flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3" /> Unsaved recordings ({pendingUploads.length})
                </div>
                {pendingUploads.map((row) => (
                  <div
                    key={row.id}
                    className="bg-background/40 rounded p-2 mb-1 text-[11px]"
                    data-testid={`pending-upload-${row.id}`}
                  >
                    <div className="font-medium truncate">
                      Recording from {fmtDateTime(row.recorded_at)}
                    </div>
                    <div className="text-muted-foreground">
                      {Math.round((row.size_bytes || 0) / 1024)} KB · saved locally
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      <Button
                        size="sm"
                        className="h-6 text-[10px] px-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border-0"
                        disabled={retryingId === row.id || uploading}
                        onClick={() => retryPending(row)}
                        data-testid={`retry-upload-${row.id}`}
                      >
                        {retryingId === row.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                        Retry upload
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-[10px] px-2 text-muted-foreground hover:text-destructive"
                        disabled={retryingId === row.id}
                        onClick={() => discardPending(row)}
                        data-testid={`discard-upload-${row.id}`}
                      >
                        Discard
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && notes.length === 0 && pendingUploads.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
                No meetings recorded yet
              </div>
            )}
            {notes.map((n) => {
              const st = STATUS_LABELS[n.processing_status] || STATUS_LABELS.queued;
              return (
                <button
                  key={n.id}
                  data-testid={`meeting-note-row-${n.id}`}
                  onClick={() => setActiveId(n.id)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    activeId === n.id
                      ? 'bg-muted'
                      : 'hover:bg-muted/40'
                  }`}
                >
                  <div className="text-sm font-medium truncate">
                    {n.ai_title || 'Untitled Meeting'}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                    <span>{fmtDateTime(n.recorded_at)}</span>
                    {n.audio_duration_sec > 0 && <span>· {fmtDuration(n.audio_duration_sec)}</span>}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${st.color}`}>{st.label}</div>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </aside>

      {/* Main: active note 3-tab view */}
      <main className="flex-1 overflow-hidden">
        {!activeNote ? (
          <div className="h-full flex items-center justify-center text-center text-muted-foreground p-8">
            <div>
              <Sparkles className="h-10 w-10 mx-auto mb-3 text-gold opacity-60" />
              <p className="text-sm">Hit <span className="text-foreground font-medium">Start Recording</span> or pick a meeting on the left.</p>
            </div>
          </div>
        ) : (
          <ActiveNoteView
            note={activeNote}
            onChanged={(updated) => {
              setActiveNote(updated);
              refreshList();
            }}
            onDeleted={() => {
              setActiveId(null);
              setActiveNote(null);
              refreshList();
            }}
          />
        )}
      </main>
    </div>
  );
}


function ActiveNoteView({ note, onChanged, onDeleted }) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [tab, setTab] = useState('summary');
  const [titleDraft, setTitleDraft] = useState(note.ai_title || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const notesTimerRef = useRef(null);

  const isRecorder = note.recorded_by === user?.id;
  const sharedWith = note.shared_with || [];
  const isRestricted = sharedWith.length > 0;

  // D80 — manual_notes was a string; now stored as TipTap JSON. Convert
  // legacy string content into a paragraph doc on load so old notes still
  // render in the new block editor.
  const toEditorDoc = (val) => {
    if (val && typeof val === 'object' && val.type === 'doc') return val;
    if (typeof val === 'string' && val.trim()) {
      return {
        type: 'doc',
        content: val.split('\n').map(line => ({
          type: 'paragraph',
          ...(line ? { content: [{ type: 'text', text: line }] } : {}),
        })),
      };
    }
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  };
  const initialDoc = toEditorDoc(note.manual_notes);
  const [notesDoc, setNotesDoc] = useState(initialDoc);

  // Sync local state when active note changes
  useEffect(() => {
    setTitleDraft(note.ai_title || '');
    setNotesDoc(toEditorDoc(note.manual_notes));
  }, [note.id, note.ai_title, note.manual_notes]);

  const st = STATUS_LABELS[note.processing_status] || STATUS_LABELS.queued;
  const isReady = note.processing_status === 'ready';
  const isFailed = note.processing_status === 'failed';

  const saveTitle = async () => {
    if (!titleDraft.trim() || titleDraft === note.ai_title) return;
    try {
      const res = await meetingNotesAPI.update(note.id, { ai_title: titleDraft.trim() });
      onChanged({ ...note, ...res.data });
    } catch {
      toast.error('Failed to save title');
    }
  };

  // Auto-save manual notes 0.8s after the user stops editing.
  const onNotesChange = (doc) => {
    setNotesDoc(doc);
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(async () => {
      setSavingNotes(true);
      try {
        await meetingNotesAPI.update(note.id, { manual_notes: doc });
        onChanged({ ...note, manual_notes: doc });
      } catch {
        // silent
      } finally {
        setSavingNotes(false);
      }
    }, 800);
  };

  const downloadAudio = () => {
    window.open(meetingNotesAPI.audioUrl(note.id), '_blank');
  };

  const deleteNote = async () => {
    if (!window.confirm('Delete this meeting note and its audio? This cannot be undone.')) return;
    try {
      await meetingNotesAPI.delete(note.id);
      toast.success('Deleted');
      onDeleted();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="active-note-view">
      {/* Header */}
      <div className="border-b border-border p-4 space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            data-testid="note-title-input"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={saveTitle}
            placeholder="Untitled Meeting"
            className="text-lg font-semibold border-transparent bg-transparent hover:bg-muted/30 focus:bg-muted/40 flex-1"
          />
          <div className={`text-xs ${st.color} flex items-center gap-1.5`}>
            {!isReady && !isFailed && <Loader2 className="h-3 w-3 animate-spin" />}
            {st.label}
          </div>
          <Button
            data-testid="download-audio-btn"
            variant="outline" size="sm"
            disabled={!note.audio_url}
            onClick={downloadAudio}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Audio
          </Button>
          <Button
            data-testid="delete-note-btn"
            variant="ghost" size="sm"
            onClick={deleteNote}
            className="text-red-400 hover:text-red-300"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
          <span>{fmtDateTime(note.recorded_at)}</span>
          {note.audio_duration_sec > 0 && <span>· {fmtDuration(note.audio_duration_sec)}</span>}
          {note.recorded_by_name && <span>· by {note.recorded_by_name}</span>}
          {note.transcript_language && note.transcript_language !== 'auto' && (
            <span>· lang: {note.transcript_language}</span>
          )}
          {/* D74 — Visibility indicator */}
          {isRestricted ? (
            <span className="inline-flex items-center gap-1 text-gold/70">
              · <Users className="h-3 w-3" /> Shared with {sharedWith.length}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-muted-foreground/60">
              · <Lock className="h-3 w-3" /> Team-visible
            </span>
          )}
        </div>
        {/* D74 — Share control. Recorder only. */}
        {isRecorder && (
          <div className="pt-1" data-testid={`meeting-share-bar-${note.id}`}>
            <ShareWithControl
              workspaceId={currentWorkspace?.id}
              noteId={note.id}
              value={sharedWith}
              excludeUserId={user?.id}
              emptyLabel="Team-visible (everyone in workspace can see)"
              onChange={async (newIds) => {
                const res = await meetingNotesAPI.setShare(note.id, newIds);
                onChanged({ ...note, shared_with: res.data.shared_with });
              }}
            />
          </div>
        )}
        {isFailed && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300 flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium">Processing failed</div>
              {note.error && <div className="text-[11px] opacity-80 mt-0.5">{note.error}</div>}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-200"
              data-testid="meeting-retry-btn"
              onClick={async () => {
                try {
                  await meetingNotesAPI.retry(note.id);
                  toast.success('Retrying transcription…');
                  onChanged({ ...note, processing_status: 'queued', error: '' });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Retry failed');
                }
              }}
            >
              <Sparkles className="h-3 w-3 mr-1" /> Retry
            </Button>
          </div>
        )}
        {note.summary_warning && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            {note.summary_warning}
          </div>
        )}
      </div>

      {/* 3-tab body */}
      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="mx-4 mt-2 self-start" data-testid="note-tabs">
          <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="transcript" data-testid="tab-transcript">Transcript</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="flex-1 overflow-hidden m-0">
          <ScrollArea className="h-full">
            <div className="p-6 max-w-3xl mx-auto" data-testid="summary-tab-content">
              {!isReady && !isFailed && (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {st.label}
                </div>
              )}
              {isReady && (
                <>
                  {note.action_items && note.action_items.length > 0 && (
                    <section className="mb-8" data-testid="action-items-section">
                      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-gold uppercase tracking-wider">
                        <ListChecks className="h-4 w-4" />
                        Action Items &amp; Next Steps
                      </h3>
                      <ul className="space-y-2">
                        {note.action_items.map((a, i) => (
                          <li
                            key={i}
                            data-testid={`action-item-${i}`}
                            className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
                          >
                            <CheckSquare className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                            <div className="flex-1 text-sm">
                              <div>{a.text}</div>
                              <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
                                <span className="rounded-full bg-gold/10 text-gold px-2 py-0.5">
                                  {a.assignee || 'Unassigned'}
                                </span>
                                {a.due_hint && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> {a.due_hint}
                                  </span>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {note.summary_sections && note.summary_sections.length > 0 ? (
                    note.summary_sections.map((s, i) => (
                      <section key={i} className="mb-6" data-testid={`summary-section-${i}`}>
                        <h3 className="text-base font-semibold mb-2">{s.heading}</h3>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-foreground/90">
                          {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                        </ul>
                      </section>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground italic">
                      No summary sections available.
                    </div>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="notes" className="flex-1 overflow-hidden m-0">
          <div className="p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Your notes alongside the meeting
              </h3>
              {savingNotes && <span className="text-[11px] text-muted-foreground">Saving…</span>}
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="manual-notes-editor">
              <NotionEditor
                key={note.id}
                content={notesDoc}
                onUpdate={onNotesChange}
                placeholder="Jot down your own thoughts, follow-ups, or context here… Type '/' for blocks"
                workspaceId={note.workspace_id || ''}
                onMention={(m) => {
                  // D80 Tier 2 — in-app notification only. NO emails.
                  try {
                    const token = localStorage.getItem('stellar_token');
                    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/mention-notify`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                      body: JSON.stringify({
                        workspace_id: note.workspace_id,
                        mentioned_user_id: m.id,
                        note_id: note.id,
                        note_title: note.title || 'a meeting note',
                        surface: 'meeting_note',
                      }),
                    }).catch(() => {});
                  } catch {}
                }}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="transcript" className="flex-1 overflow-hidden m-0">
          <ScrollArea className="h-full">
            <div className="p-6 max-w-3xl mx-auto space-y-3" data-testid="transcript-tab-content">
              {(!note.transcript_paragraphs || note.transcript_paragraphs.length === 0) ? (
                <div className="text-sm text-muted-foreground italic">
                  {isReady ? 'Transcript not available.' : `${st.label}…`}
                </div>
              ) : (
                note.transcript_paragraphs.map((p, i) => (
                  <p key={i} data-testid={`transcript-paragraph-${i}`} className="text-sm leading-relaxed">
                    {p}
                  </p>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
